import { createRouter, createWebHistory } from 'vue-router';
import HomeView from '@/views/HomeView.vue';
import BlogView from '@/views/BlogView.vue';
import ServiceRequestView from '@/views/ServiceRequestView.vue';
import ServiceDetailView from '@/views/ServiceDetailView.vue';
import BlogPostView from '@/views/BlogPostView.vue';
import NotFoundView from '@/views/NotFoundView.vue';
import ServiceOptionsView from '@/views/ServiceOptionsView.vue';
import MaintainedUnitsView from '@/views/MaintainedUnitsView.vue';
import MaintainedUnitAccessView from '@/views/MaintainedUnitAccessView.vue';
import ManagedMaintenanceExplainedView from '@/views/ManagedMaintenanceExplainedView.vue';
import MaintenanceEstimateView from '@/views/MaintenanceEstimateView.vue';
import MachinesForSaleView from '@/views/MachinesForSaleView.vue';
import MachineForSaleView from '@/views/MachineForSaleView.vue';
import { adminOrigin, staffDestination } from '@/data/siteOrigins';

const routes = [
  { path: '/sell/:token', component: () => import('@/components/SaleEditor.vue'), props: true, meta: { title: 'Create your FireSale listing' } },
  { path: '/m/login', component: () => import('@/views/MobileLoginView.vue'), meta: { title: 'Centerfire Services | Open the app' } },
  { path: '/', name: 'home', component: HomeView, meta: { title: 'Centerfire Services | Mobile Mechanics and Welding' } },
  { path: '/blog', name: 'blog', component: BlogView, meta: { title: 'Centerfire Services | Blog' } },
  {
    path: '/services-request',
    name: 'service-request',
    component: ServiceRequestView,
    meta: { title: 'Centerfire Services | Service Request' },
  },
  {
    path: '/service-options',
    name: 'service-options',
    component: ServiceOptionsView,
    meta: { title: 'Centerfire Services | Which Service Do I Need?' },
  },
  {
    path: '/maintunits',
    name: 'maintained-units',
    component: MaintainedUnitsView,
    meta: { title: 'Centerfire Services | Maintained Units' },
  },
  {
    path: '/maintunits/c/:portalToken',
    name: 'maintained-customer-access',
    component: MaintainedUnitAccessView,
    meta: { title: 'Centerfire Services | Protected Fleet Records' },
  },
  {
    // The private per-machine link for machines that will never carry a scannable tag.
    path: '/maintunits/m/:machineToken',
    name: 'maintained-machine-link',
    component: MaintainedUnitAccessView,
    meta: { title: 'Centerfire Services | Machine Record' },
  },
  {
    path: '/maintunits/:unitId',
    name: 'maintained-unit-access',
    component: MaintainedUnitAccessView,
    meta: { title: 'Centerfire Services | Protected Machine Record' },
  },
  { path: '/admin/:pathMatch(.*)*', beforeEnter: (to) => { window.location.replace(`${adminOrigin}${staffDestination(to.fullPath)}`); return false; } },
  {
    path: '/forsale',
    name: 'sale-units',
    component: MachinesForSaleView,
    meta: { title: 'Centerfire Services | Center FireSale' },
  },
  {
    // Addressed by machine, not by number - see data/machinesForSale.js.
    path: '/forsale/:slug',
    name: 'sale-unit',
    component: MachineForSaleView,
    meta: { title: 'Centerfire Services | Sale Unit' },
  },
  {
    path: '/maintenance-estimate',
    name: 'maintenance-estimate',
    component: MaintenanceEstimateView,
    meta: { title: 'Centerfire Services | Maintenance Plan Estimate' },
  },
  {
    path: '/managed-maintenance-explained',
    name: 'managed-maintenance-explained',
    component: ManagedMaintenanceExplainedView,
    meta: { title: 'Centerfire Services | How Managed Maintenance Works' },
  },
  { path: '/services/off-hwy-equipment-service', redirect: '/services/managed-maintenance-systems' },
  { path: '/services/welding-post', redirect: '/services/mobile-welding' },
  { path: '/services/auto-repair', redirect: '/services/on-site-repairs' },
  { path: '/services/fleet-services', redirect: '/services/additional-capabilities' },
  { path: '/services/:slug', name: 'service-detail', component: ServiceDetailView, meta: { title: 'Centerfire Services | Service' } },
  { path: '/blog-posts/:slug', name: 'blog-post', component: BlogPostView, meta: { title: 'Centerfire Services | Blog Post' } },
  { path: '/search', redirect: '/services-request' },
  { path: '/privacy', name: 'privacy', component: () => import('@/views/LegalView.vue'), meta: { title: 'Centerfire Services | Privacy Policy', doc: 'privacy' } },
  { path: '/terms', name: 'terms', component: () => import('@/views/LegalView.vue'), meta: { title: 'Centerfire Services | Terms of Use and EULA', doc: 'terms' } },
  { path: '/privacy-policy', redirect: '/privacy' },
  { path: '/eula', redirect: '/terms' },
  { path: '/:pathMatch(.*)*', name: 'not-found', component: NotFoundView, meta: { title: 'Centerfire Services | Not Found' } },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    if (to.hash) {
      return { el: to.hash, top: 270, behavior: 'smooth' };
    }

    return { top: 0 };
  },
});

router.afterEach((to) => {
  document.title = to.meta.title || 'Centerfire Services';
});

export default router;
